// src/hooks/useNavigationPermissions.ts
import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { HOME_PERMISSIONS, NAVIGATION_PERMISSIONS } from '../constants/permissions.constants';

/**
 * Hook to get all navigation-related permissions in one object
 *
 * Benefits:
 * - Single hook call instead of multiple usePermission calls
 * - Centralized permission management for navigation
 * - Memoized to prevent unnecessary re-renders
 * - Type-safe permission checks
 *
 * @returns Object with all navigation permission flags
 *
 * @example
 * ```tsx
 * const navPerms = useNavigationPermissions();
 * if (navPerms.canProducts) {
 *   // Show products menu
 * }
 * ```
 */
export const useNavigationPermissions = () => {
  // Check all navigation permissions
  const canHomeDashboard = usePermission(NAVIGATION_PERMISSIONS.HOME);
  const canHomePendingWork = usePermission(HOME_PERMISSIONS.PENDING_WORK_VIEW);
  const canHomeMyActivity = usePermission(HOME_PERMISSIONS.MY_ACTIVITY_VIEW);
  // Inicio is reachable with any of its sections (global dashboard, pending bar, my activity).
  const canHome = canHomeDashboard || canHomePendingWork || canHomeMyActivity;
  const canProducts = usePermission(NAVIGATION_PERMISSIONS.PRODUCTS);
  const canInventoryRegister = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_REGISTER);
  const canInventoryWarehouse = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_WAREHOUSE);
  const canInventoryTransfer = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_TRANSFER);
  const canInventoryRequest = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_REQUEST);
  const canInventoryBranch = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_BRANCH);
  const canInventoryGeneral = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_GENERAL);
  const canSafetyStock = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_SAFETY_STOCK);
  const canPhysicalCount = usePermission(NAVIGATION_PERMISSIONS.INVENTORY_PHYSICAL_COUNT);
  const canSales = usePermission(NAVIGATION_PERMISSIONS.SALES);
  const canSalesByPiece = usePermission(NAVIGATION_PERMISSIONS.SALES_BY_PIECE);
  const canSellerManagement = usePermission(NAVIGATION_PERMISSIONS.SELLER_MANAGEMENT);
  const canCrmClientManagement = usePermission(NAVIGATION_PERMISSIONS.CRM_CLIENT_MANAGEMENT);
  const canReportsProductHistory = usePermission(NAVIGATION_PERMISSIONS.REPORTS_PRODUCT_HISTORY);
  const canReportsStatistics = usePermission(NAVIGATION_PERMISSIONS.REPORTS_STATISTICS);
  const canSettingsProps = usePermission(NAVIGATION_PERMISSIONS.SETTINGS_PROPERTIES);
  const canSettingsRoles = usePermission(NAVIGATION_PERMISSIONS.SETTINGS_ROLES);
  const canSettingsUsers = usePermission(NAVIGATION_PERMISSIONS.SETTINGS_USERS);
  const canSystem = usePermission(NAVIGATION_PERMISSIONS.SYSTEM);

  // Memoize the result to prevent unnecessary re-renders
  // This object is stable unless one of the permissions changes
  return useMemo(
    () => ({
      canHome,
      canHomeDashboard,
      canHomePendingWork,
      canHomeMyActivity,
      canProducts,
      canInventoryRegister,
      canInventoryWarehouse,
      canInventoryTransfer,
      canInventoryRequest,
      canInventoryBranch,
      canInventoryGeneral,
      canSafetyStock,
      canPhysicalCount,
      canSales,
      canSalesByPiece,
      canSellerManagement,
      canCrmClientManagement,
      canReportsProductHistory,
      canReportsStatistics,
      canSettingsProps,
      canSettingsRoles,
      canSettingsUsers,
      canSystem,
    }),
    [
      canHome,
      canHomeDashboard,
      canHomePendingWork,
      canHomeMyActivity,
      canProducts,
      canInventoryRegister,
      canInventoryWarehouse,
      canInventoryTransfer,
      canInventoryRequest,
      canInventoryBranch,
      canInventoryGeneral,
      canSafetyStock,
      canPhysicalCount,
      canSales,
      canSalesByPiece,
      canSellerManagement,
      canCrmClientManagement,
      canReportsProductHistory,
      canReportsStatistics,
      canSettingsProps,
      canSettingsRoles,
      canSettingsUsers,
      canSystem,
    ],
  );
};
